import { Component, computed, inject, input, output } from "@angular/core";
import { SettingsCursorsCollection } from "@components/settings-cursors-collection/settings-cursors-collection";
import { EDUCATION_LEVEL_GROUPS } from "@config";
import { UserSchoolGrade } from "@domain/User";
import { AuthState } from "@states/auth.state";

const SCHOOL_GRADE_LABELS: Record<UserSchoolGrade, string> = {
  1: "Primaire 1",
  2: "Primaire 2",
  3: "Primaire 3",
  4: "Primaire 4",
  5: "Primaire 5",
  6: "Primaire 6",
  7: "Secondaire 1",
  8: "Secondaire 2",
  9: "Secondaire 3",
  10: "Secondaire 4",
  11: "Secondaire 5",
  12: "Postsecondaire",
};

@Component({
  selector: "app-settings-panel",
  standalone: true,
  imports: [SettingsCursorsCollection],
  template: `
    @if (isOpen()) {
      <div class="backdrop" (click)="closed.emit()"></div>
    }
    <div class="panel" [class.panel--open]="isOpen()" [attr.aria-hidden]="!isOpen() ? true : null">
      @if (isLoggedIn()) {
        <div class="panel__header">
          <div class="panel__avatar">
            <img src="assets/icons/flamme.svg" alt="" class="panel__avatar-img" />
          </div>
          <div class="panel__user-info">
            <p class="panel__username">{{ username() }}</p>
            @if (educationLevelName()) {
              <p class="body-3 panel__education-level">{{ educationLevelName() }}</p>
            }
          </div>
          <button class="btn-icon btn-primary" (click)="closed.emit()">
            <img src="assets/icons/close.svg" alt="close settings" />
          </button>
        </div>
      } @else {
        <div class="panel__header panel__header--guest">
          <button class="btn-icon btn-primary" (click)="closed.emit()">
            <img src="assets/icons/close.svg" alt="close settings" />
          </button>
        </div>
      }
      <div class="panel__tabs">
        <button class="panel__tab panel__tab--active">Récompenses</button>
      </div>
      <div class="panel__content">
        <app-settings-cursors-collection [educationLevelGroup]="educationLevelGroup()" [isGuest]="!isLoggedIn()" />
      </div>
    </div>
  `,
  styles: `
    @use "responsive" as *;
    @use "typography" as *;

    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 10000;
    }

    .panel {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 25%;
      min-width: 360px;
      background: var(--color-white);
      z-index: 10001;
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
      transform: translateX(100%);
      transition: transform 0.3s ease;
      display: flex;
      flex-direction: column;

      @include screenWidth("tablet-only") {
        width: 60%;
      }

      @include screenWidth("mobile-only") {
        width: 100%;
        min-width: unset;
      }
    }

    .panel--open {
      transform: translateX(0);
    }

    .panel__header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 24px;

      &--guest {
        justify-content: flex-end;
      }
    }

    .panel__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    .panel__user-info {
      flex: 1;
      min-width: 0;
    }

    .panel__username {
      margin: 0;
      font-weight: 500;
      font-size: 16px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .panel__education-level {
      margin: 2px 0 0 0;
    }

    .panel__tabs {
      padding: 0 16px;
    }

    .panel__tab {
      @include body3();
      font-weight: 500;
      background: none;
      border: none;
      padding: 12px 16px;
      cursor: pointer;
      color: var(--color-text-secondary);
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
    }

    .panel__tab--active {
      color: var(--color-text-primary);
      border-radius: 12px 12px 0 0;
      border-top: 2px solid var(--color-cream);
      border-right: 2px solid var(--color-cream);
      border-left: 2px solid var(--color-cream);
      border-bottom: 2px solid var(--color-light-cream);
      background: var(--color-light-cream);
      position: relative;
      z-index: 1;
      margin-bottom: -2px;
    }

    .panel__content {
      overflow-y: auto;
      flex: 1;
    }
  `,
})
export class SettingsPanel {
  private authState = inject(AuthState);

  isOpen = input.required<boolean>();
  closed = output<void>();

  protected isLoggedIn = computed(() => !!this.authState.currentUser());
  protected username = computed(() => this.authState.currentUser()?.username ?? "");
  protected educationLevelName = computed(() => {
    const grade = this.authState.currentUser()?.schoolGrade;
    if (!grade) return undefined;
    return SCHOOL_GRADE_LABELS[grade];
  });
  protected educationLevelGroup = computed(() => this.authState.currentUser()?.educationLevelGroup ?? EDUCATION_LEVEL_GROUPS[0]);
}
